import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useController, useForm, useWatch } from 'react-hook-form'

import { useSelectedProject } from '@/api/models/main/hooks/useSelectedProject'
import { useSelectedProjectPremises } from '@/api/models/main/hooks/useSelectedProjectPremises'
import { useQuickIntervals } from '@/api/models/parking/hooks/useQuickIntervals'
import { TariffType } from '@/api/models/parking/lib/parking.types'
import { RequestByIdApiType } from '@/api/models/requests'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ScreenSize } from '@/app/constants'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import {
  getServerNow,
  useServerTimeStore
} from '@/components/entities/common/model/server-time'
import { PassAutosFormBlock } from '@/components/entities/passes/ui/PassForms/FormBlock/PassAutosFormBlock'
import { RequestRoomPickerFormBlock } from '@/components/entities/requests/ui/FormBlock/RequestRoomPickerFormBlock'
import { useFormDirty } from '@/components/entities/requests/ui/hooks/useFormDirty'
import {
  getDefaultAutos,
  getDefaultRoom
} from '@/components/entities/requests/ui/model/utils'
import SomethingWentWrong from '@/components/pages/plugs/SomethingWentWrong'
import {
  CalendarType,
  DateRange
} from '@/components/shared/datepicker/ui/Calendar.types'
import { CalendarDropdown } from '@/components/shared/datepicker/ui/CalendarDropdown'
import { getDateLabel } from '@/components/shared/datepicker/utils/getDateLabel'
import { getFirstBookableHour } from '@/components/shared/datepicker/utils/getFirstBookableHour'
import { DrawerFormLayout } from '@/components/shared/drawer-form-layout'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { hasPeriodServerError } from '@/utils/parseServerErrorField'

import {
  AutosFormData,
  AutosFormErrors,
  generateAutoPassFormSchema
} from '../../../../requests/ui/model/schemas'
import { FooterWithPrice } from './FooterWithPrice'
import { useAutoPassAvailability } from './hooks/useAutoPassAvailability'
import { useBookingFormData } from './hooks/useBookingFormData'
import { useParkingTariffs } from './hooks/useParkingTariffs'
import { useRentCalendar } from './hooks/useRentCalendar'
import {
  getMskCalendarToday,
  toApiDate,
  toApiUtcDateTime,
  toDailyInputDisplayDate,
  toMskCalendarDay
} from './lib/dates'
import { PeriodStatusMessage } from './PeriodStatusMessage'
import { RentPassAutoSkeleton } from './RentPassAutoSkeleton'
import { Summary } from './Summary/Summary'
import { TariffTabs } from './TariffTabs'
import { UnavailableParkingScreen } from './UnavailableParkingScreen'

type Props = {
  onFormDirty: (_: boolean) => void
  pass?: RequestByIdApiType
  personalAccountId?: string
}

type RentPassAutoFormData = AutosFormData & {
  period?: DateRange
}

const RentPassAutoForm: FC<Props> = ({
  onFormDirty,
  pass,
  personalAccountId: defaultPersonalAccountId
}) => {
  useServerTimeStore(state => state.serverOffsetMs)
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { reachGoal } = useYandexMetrika()

  const [selectedTab, setSelectedTab] = useState<TariffType>(TariffType.Daily)
  const periodStatusRef = useRef<HTMLDivElement>(null)
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)

  const form = useForm<RentPassAutoFormData, AutosFormErrors>({
    defaultValues: {
      room: undefined,
      period: { from: new Date(), to: new Date() },
      autos: [{ number: '', model: '', isStandard: true }]
    },
    mode: 'onSubmit',
    resolver: yupResolver(generateAutoPassFormSchema())
  })

  const {
    formState,
    handleSubmit,
    reset,
    control,
    setValue,
    setError,
    clearErrors
  } = form
  const { field: dateField } = useController({ control, name: 'period' })
  const period = dateField.value ?? {}

  const room = useWatch({ control, name: 'room' })
  const personalAccount = useWatch({ control, name: 'personalAccount' })
  const autos = useWatch({ control, name: 'autos' })
  const primaryAuto = autos?.[0]

  const slotsFilter = useMemo(
    () => ({
      autoNumber: primaryAuto?.number?.trim() || undefined,
      isStandard: primaryAuto?.isStandard ?? true
    }),
    [primaryAuto?.isStandard, primaryAuto?.number]
  )

  const formattedPeriod = useMemo(() => {
    const isDaily = selectedTab === TariffType.Daily
    const from = isDaily
      ? toMskCalendarDay(period.from).startOf('day').toDate()
      : period.from
    const to = isDaily
      ? toMskCalendarDay(period.to).startOf('day').add(1, 'day').toDate()
      : period.to

    return {
      from: toApiUtcDateTime(from),
      to: toApiUtcDateTime(to)
    }
  }, [period.from, period.to, selectedTab])

  const { selectedProject } = useSelectedProject()
  const { rooms } = useSelectedProjectPremises()
  const { data: quickIntervals } = useQuickIntervals()

  const formData = useBookingFormData({
    roomId: room?.id,
    selectedPersonalAccountId: personalAccount?.id,
    defaultPersonalAccountId,
    selectedProject,
    from: formattedPeriod.from,
    to: formattedPeriod.to,
    type: selectedTab,
    autoModel: primaryAuto?.model,
    autoNumber: primaryAuto?.number,
    isStandard: primaryAuto?.isStandard
  })

  const selectedIntervalCount =
    period.from && period.to
      ? dayjs(period.to).diff(
          dayjs(period.from),
          selectedTab === TariffType.Hourly ? 'hour' : 'day'
        )
      : 0

  const { tariffs, isLoading, isError, tabs, priceForInterval } =
    useParkingTariffs(selectedTab, selectedIntervalCount)

  const {
    isCalendarOpen,
    isServerTimeLoading,
    slotsPeriod,
    setSlotsPeriod,
    getDefaultPeriod,
    getCalendarIntervals,
    handleCalendarOpenChange,
    handleCalendarMonthChange,
    onQuickFilterClick
  } = useRentCalendar({
    selectedTab,
    period,
    quickIntervals
  })

  const {
    summary,
    refetchSummary,
    isSummaryError,
    isConflictError,
    conflictMessage,
    isSummaryValidationError,
    getAutoPassError,
    isParkingSlotsLoading,
    isParkingSlotsFetching,
    individualDaysToSelect
  } = useAutoPassAvailability({
    selectedTab,
    from: formattedPeriod.from,
    to: formattedPeriod.to,
    slotsPeriod,
    autoNumber: slotsFilter.autoNumber,
    isStandard: slotsFilter.isStandard,
    personalAccountId: formData.personalAccountId,
    isCalendarOpen
  })

  const autoPassError = getAutoPassError()

  const applyPeriodServerError = useCallback(() => {
    const serverErrors = autoPassError?.response?.data?.errors
    const periodError = serverErrors?.find(error =>
      hasPeriodServerError(error.field)
    )

    if (periodError?.message) {
      setError('period', {
        type: 'server',
        message: periodError.message
      })
      return
    }

    if (isConflictError && conflictMessage) {
      setError('period', {
        type: 'server',
        message: conflictMessage
      })
    }
  }, [autoPassError, conflictMessage, isConflictError, setError])

  useEffect(() => {
    applyPeriodServerError()
  }, [applyPeriodServerError])

  const isSummaryScreen = searchParams?.get('step') === 'summary'

  const defaultPeriod = useMemo(
    () => getDefaultPeriod(selectedTab),
    [selectedTab, getDefaultPeriod]
  )

  const formStateForDirty = useMemo(() => {
    if (!period.from || !period.to) {
      return formState
    }

    const isDefaultPeriod =
      dayjs(period.from).valueOf() === dayjs(defaultPeriod.from).valueOf() &&
      dayjs(period.to).valueOf() === dayjs(defaultPeriod.to).valueOf()

    if (!isDefaultPeriod || !formState.dirtyFields.period) {
      return formState
    }

    const { period: _period, ...dirtyFields } = formState.dirtyFields

    return {
      ...formState,
      dirtyFields
    }
  }, [defaultPeriod.from, defaultPeriod.to, formState, period.from, period.to])

  useEffect(() => {
    if (tabs.length === 0 || tabs.some(tab => tab.key === selectedTab)) {
      return
    }

    const nextTab = tabs[0].key
    clearErrors('period')
    setSelectedTab(nextTab)
  }, [clearErrors, selectedTab, tabs])

  useFormDirty(onFormDirty, formStateForDirty)

  useEffect(() => {
    reset({
      room: getDefaultRoom(rooms, pass?.premiseId),
      autos: getDefaultAutos(pass, undefined)
    })
  }, [pass, reset, rooms])

  useEffect(() => {
    setValue('period', getDefaultPeriod(selectedTab), { shouldDirty: true })
  }, [pass, rooms, selectedTab, getDefaultPeriod, setValue])

  const handlePriceSubmit = handleSubmit(async () => {
    const params = new URLSearchParams(searchParams?.toString())

    const result = await refetchSummary()

    if (result.isSuccess) {
      reachGoal(YandexMetrikaEvent.PASSES_RENT_CALCULATE)

      params.set('step', 'summary')
      router.push(`${pathname}?${params}`)
    }
  })

  const handleDateRangeChange = (range?: DateRange) => {
    clearErrors('period')
    setValue('period', range, { shouldDirty: true })

    if (range?.from && range?.to) {
      setSlotsPeriod({
        from: toApiDate(range.from),
        to: toApiDate(range.to)
      })
    }
  }

  const handleCalendarRangeApply = () => {
    if (selectedTab === TariffType.Daily) {
      reachGoal(YandexMetrikaEvent.PASSES_RENT_APPLY_CALENDAR_DAYS)
    } else {
      reachGoal(YandexMetrikaEvent.PASSES_RENT_APPLY_CALENDAR_HOURS)
    }
  }

  const formatDailyInputValue = useCallback(
    (range: DateRange) =>
      getDateLabel({
        from: range.from,
        to: range.to && toDailyInputDisplayDate(range.to)
      }),
    []
  )

  const onTabChange = (tab: TariffType) => {
    clearErrors('period')
    setValue('period', getDefaultPeriod(tab), { shouldDirty: true })
    setSelectedTab(tab)
  }

  useEffect(() => {
    //при перезагрузке страницы сбрасываем на форму аренды т.к. summary больше не доступен
    if (isSummaryScreen && !summary) {
      const params = new URLSearchParams(searchParams?.toString())
      params.delete('step')
      router.replace(`${pathname}?${params}`)
    }
  }, [isSummaryScreen, summary, pathname, router, searchParams])

  if (isSummaryScreen && summary) {
    return (
      <Summary
        formData={formData}
        data={summary}
        date={period}
        tariffType={selectedTab}
      />
    )
  }

  if (isLoading) {
    return <RentPassAutoSkeleton />
  }

  if (tariffs?.length === 0) {
    return <UnavailableParkingScreen />
  }

  if (
    isError ||
    (isSummaryError && !isConflictError && !isSummaryValidationError)
  ) {
    return <SomethingWentWrong />
  }

  const calendarIntervals = getCalendarIntervals()
  const parkingToday = getMskCalendarToday()
  const firstBookableDate = getFirstBookableHour(getServerNow()).toDate()

  return (
    <DrawerFormLayout
      onSubmit={() => undefined}
      scrollMode={isCalendarOpen && !isMobileScreen ? 'form' : 'content'}
      customFooter={
        <FooterWithPrice
          type={selectedTab}
          onSubmit={handlePriceSubmit}
          price={priceForInterval}
          loading={formState.isSubmitting}
        />
      }
    >
      <div className="form-block-cover mb-16 grid grid-cols-1 gap-24 p-32 lg:gap-16 lg:p-16">
        <FormProvider {...form}>
          <RequestRoomPickerFormBlock ownerOnly />
        </FormProvider>
      </div>
      <FormProvider {...form}>
        <PassAutosFormBlock
          typeAlias={RequestTypeAlias.RentPassAuto}
          passError={autoPassError}
          singleCarMode={true}
        />
      </FormProvider>
      <div className="form-block-cover my-16 p-32 lg:p-16">
        {tabs.length > 1 && (
          <TariffTabs
            tabs={tabs}
            selectedTab={selectedTab}
            onTabChange={onTabChange}
          />
        )}
        <CalendarDropdown
          autoScrollTargetRef={isMobileScreen ? undefined : periodStatusRef}
          dropdownClassName="-mt-4"
          calendarClassName={
            isMobileScreen
              ? 'max-w-[314px] xxs:max-w-full'
              : 'relative max-w-[314px] xxs:max-w-full'
          }
          placeholder={
            selectedTab === TariffType.Daily
              ? 'Период аренды'
              : 'Интервал аренды'
          }
          type={CalendarType.Range}
          calendarType={selectedTab === TariffType.Daily ? 'calendar' : 'hours'}
          loading={
            isParkingSlotsLoading ||
            isParkingSlotsFetching ||
            isServerTimeLoading
          }
          onOpenChange={handleCalendarOpenChange}
          onMonthChange={handleCalendarMonthChange}
          dateRange={period}
          formatInputValue={
            selectedTab === TariffType.Daily ? formatDailyInputValue : undefined
          }
          showQuickFilters={calendarIntervals.length > 0}
          onRangeChange={handleDateRangeChange}
          onRangeApply={handleCalendarRangeApply}
          customQuickFilters={calendarIntervals}
          firstVisibleDate={
            selectedTab === TariffType.Hourly ? firstBookableDate : undefined
          }
          fromMonth={parkingToday}
          individualDaysToSelect={individualDaysToSelect}
          disabled={{
            before: parkingToday
          }}
          onFilterClick={onQuickFilterClick}
          invalid={Boolean(formState.errors.period)}
        />
        <div ref={periodStatusRef}>
          <PeriodStatusMessage
            periodTo={period.to}
            isDaily={selectedTab === TariffType.Daily}
            errorMessage={formState.errors.period?.message}
          />
        </div>
      </div>
    </DrawerFormLayout>
  )
}

export default RentPassAutoForm
